import { Box, Typography } from '@mui/material';
import StoreContext from 'contexts/storeContext';
import { useContext, useEffect, useState } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import { GREEN_BP, GREEN_LIGHT } from 'theme';
import { StoreMetricData } from 'types/storeMetrics';
import { formatUnitAndValue, getDateFromString, getDateString } from 'utils';
import { Interval } from 'types/daysAndInterval';
import DashboardDescription from 'components/Dashboard/DashboardDescription';

type Props = {
  data: StoreMetricData;
  unit: string;
  interval: Interval;
};

export default function DashboardLineChart({ data, unit, interval }: Props) {
  const { formatNumber } = useContext(StoreContext);
  const [series, setSeries] = useState<number[]>([]);
  const [xAxis, setXAxis] = useState<string[]>([]);

  const title = formatUnitAndValue(unit, formatNumber(data.total));

  useEffect(() => {
    const empty = data.data.every((point) => point.value === 0);
    const series = empty ? [] : data.data.map((point) => point.value);
    setSeries(series);
    // always setting the complete xAxis, even if data is empty, so that x labels are shown behind the empty data overlay
    setXAxis(data.data.map((point) => point.date));
  }, [data]);

  return (
    <Box sx={{ mt: 1.2, flex: 1 }}>
      <DashboardDescription sx={{ mb: -0.2 }}>Total del período</DashboardDescription>
      <Typography sx={{ fontWeight: 'bold', fontSize: '1.4rem', color: GREEN_BP }}>{title}</Typography>
      <LineChart
        sx={{ mt: -7 }}
        series={[
          {
            data: series,
            area: true,
            showMark: false,
            valueFormatter: (value) => (value ? formatUnitAndValue(unit, formatNumber(value)) : ''),
          },
        ]}
        xAxis={[
          {
            data: xAxis,
            scaleType: 'point',
            valueFormatter: (dateString: string) => {
              const date = getDateFromString(dateString);
              const options: Intl.DateTimeFormatOptions =
                interval === Interval.MONTH ? { month: 'short', year: '2-digit' } : { day: '2-digit', month: '2-digit' };
              return getDateString(date, options, true);
            },
          },
        ]}
        colors={[GREEN_LIGHT]}
        height={300}
        grid={{ vertical: true }}
        // NOTE: left margin needed for long labels to not overflow. this is enough for ""1.000.000" to fit in a half-screen chart
        margin={{ left: 70 }}
        slotProps={{
          noDataOverlay: {
            message: 'No tienes datos en este período',
          },
        }}
      />
    </Box>
  );
}
